import { hasHeimdallPermission } from '@highlight-cp/auth-client/access';
import { useHeimdallSession } from 'features/app/heimdall-session';

import { useGetHighlighterAccountQuery } from './highlighter-account-api';
import { getAccountDetailsStatus } from './profile-view/get-account-details-status';

export const useGetUser = () => {
  const heimdallSession = useHeimdallSession();
  // Guests have no Heimdall session and their cookie is HttpOnly, so always ask the API.
  const { data: account, isLoading } = useGetHighlighterAccountQuery();

  const isGuest = account?.status === 'guest';
  const isSignedIn = Boolean(heimdallSession?.data) || isGuest;
  // Stay loading while either session is read. A guest wins, so the Heimdall session's
  // refetch, pending again each time it finds no session, does not matter for one.
  const isReading = isLoading || (!isGuest && heimdallSession?.isPending);
  const heimdallUser = heimdallSession?.data?.user;
  const isRefused =
    !isGuest &&
    !!heimdallUser &&
    !hasHeimdallPermission(heimdallUser.role, { highlighter: ['use'] });
  const signedInStatus = isSignedIn ? 'authenticated' : 'unauthenticated';
  const sessionStatus = isRefused ? 'unauthorized' : signedInStatus;
  const status = isReading ? 'loading' : sessionStatus;
  const isAuthenticated = status === 'authenticated';

  const isAuthorized = isAuthenticated && !!account;
  const hasNoAccount = isAuthenticated && !account;
  const { isNewAccount, accountDetailsStatus } = getAccountDetailsStatus(account);

  // everything but active and new
  const isAccountLocked =
    isAuthorized && ['banned', 'disabled', 'deleted'].includes(account?.status ?? '');
  const isUserQuit = isAuthorized && (account?.status as string) == 'quit';

  const fullName = `${account?.firstName ?? ''} ${account?.lastName ?? ''}`;

  return {
    account,
    fullName,
    hasNoAccount,
    accountDetailsStatus,
    isAccountLocked,
    isUserQuit,
    isAuthorized,
    isLoading,
    isNewAccount,
    status,
    isGuest,
  };
};
